import React, { useState } from "react";
import { useLocation, Link } from "wouter";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { 
  Book, 
  Beaker, 
  Brain, 
  Heart, 
  Search, 
  TestTube,
  Microscope, 
  Pill, 
  Activity, 
  Menu as MenuIcon,
  X,
  ChevronRight
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";

// Define our navigation structure
interface SubItem {
  title: string;
  href: string;
  description?: string;
}

type TagType = 'general' | 'science' | 'clinical' | 'business';

interface NavItem {
  title: string;
  icon: React.ReactNode;
  href: string;
  description?: string;
  subItems?: SubItem[];
  expanded?: boolean;
  tags?: TagType[];
}

export default function LibraryPage() {
  const [location] = useLocation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState("");
  const [navOpen, setNavOpen] = useState(!isMobile);
  
  // Tag filtering state
  const [selectedTags, setSelectedTags] = useState<TagType[]>([]);
  
  // Generate path ID from the current URL or use default
  const currentPath = location.split("/")[2] || "introduction";

  // Define the navigation items structure with expandable sections
  const [navItems, setNavItems] = useState<NavItem[]>([
    {
      title: "Peptide Foundations",
      icon: <Book className="h-5 w-5" />,
      href: "peptides",
      description: "Comprehensive overview of peptide science, structure, and therapeutic applications",
      expanded: true,
      tags: ['general', 'science'],
      subItems: [
        { 
          title: "What Are Peptides?", 
          href: "peptides#what-are-peptides",
          description: "Basic definitions and structure"
        },
        { 
          title: "Peptides vs. Pharmaceuticals", 
          href: "peptides#peptides-vs-pharmaceuticals",
          description: "Comparing treatment approaches"
        },
        { 
          title: "History of Peptides", 
          href: "peptides#peptide-history",
          description: "Evolution of peptide research"
        },
        { 
          title: "Current Landscape", 
          href: "peptides#current-landscape",
          description: "Modern peptide applications"
        },
        { 
          title: "Applications", 
          href: "peptides#applications",
          description: "Medical and therapeutic uses"
        }
      ]
    },
    {
      title: "Why 2025 is the best year to start in longevity medicine",
      icon: <Book className="h-5 w-5" />,
      href: "longevity-2025",
      description: "Current trends and opportunities in longevity medicine",
      expanded: false,
      tags: ['general', 'business'],
      subItems: []
    },
    {
      title: "A longevity physician's review of Bryan Johnson's Don't Die",
      icon: <Book className="h-5 w-5" />,
      href: "bryan-johnson-review",
      description: "Professional analysis of Bryan Johnson's approach to longevity",
      expanded: false,
      tags: ['general'],
      subItems: []
    },
    {
      title: "The Top 3 mistakes people make when starting longevity practices",
      icon: <Brain className="h-5 w-5" />,
      href: "longevity-mistakes",
      description: "Common pitfalls to avoid in your longevity journey",
      expanded: false,
      tags: ['general', 'clinical'],
      subItems: []
    },
    {
      title: "When longevity medicine goes wrong: real stories and lessons",
      icon: <Activity className="h-5 w-5" />,
      href: "longevity-lessons",
      description: "Case studies and cautionary tales from the field",
      expanded: false,
      tags: ['clinical', 'business'],
      subItems: []
    }
  ]);

  // Function to toggle expand/collapse of navigation sections
  const toggleExpand = (index: number) => {
    const updatedItems = [...navItems];
    updatedItems[index].expanded = !updatedItems[index].expanded;
    setNavItems(updatedItems);
  };
  
  // Function to toggle tag selection
  const toggleTag = (tag: TagType) => {
    setSelectedTags(prevTags => {
      if (prevTags.includes(tag)) {
        return prevTags.filter(t => t !== tag);
      } else {
        return [...prevTags, tag];
      }
    });
  };

  // Filter nav items based on search and tags
  const filterByTags = (item: NavItem) => {
    if (selectedTags.length === 0) return true;
    return item.tags?.some(tag => selectedTags.includes(tag)) || false;
  };

  // Combined filtering for both search and tags
  const filteredItems = navItems.filter(filterByTags);
  
  const filteredNavItems = searchQuery
    ? filteredItems.map(item => {
        // Check if main item matches
        const mainItemMatches = item.title.toLowerCase().includes(searchQuery.toLowerCase());

        // Check if any subitems match
        const matchingSubItems = item.subItems?.filter(subItem =>
          subItem.title.toLowerCase().includes(searchQuery.toLowerCase())
        ) || [];

        // If main item or any subitems match, include this item
        if (mainItemMatches || matchingSubItems.length > 0) {
          return {
            ...item,
            expanded: true, // Expand to show matching subitems
            subItems: matchingSubItems.length > 0 ? matchingSubItems : item.subItems
          };
        }
        return null;
      }).filter(Boolean) as NavItem[]
    : filteredItems;

  // Function to handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      toast({
        title: "Empty search",
        description: "Please enter a search term",
        variant: "destructive"
      });
      return;
    }

    // For this example, we'll just filter the navigation items
    // In a real application, you would likely make an API call here
    toast({
      title: "Search results",
      description: `Found ${filteredNavItems.length} categories with matching content`
    });
  };

  // Helper function to render tags with appropriate colors
  const renderTags = (tags?: TagType[]) => {
    if (!tags || tags.length === 0) return null;
    
    const tagColors: Record<TagType, { bg: string, text: string, border: string }> = {
      'general': { bg: '#EBF5FB', text: '#3498DB', border: '#3498DB' },
      'science': { bg: '#E8F8F5', text: '#1ABC9C', border: '#1ABC9C' },
      'clinical': { bg: '#FEF9E7', text: '#F1C40F', border: '#F1C40F' },
      'business': { bg: '#F4ECF7', text: '#8E44AD', border: '#8E44AD' }
    };
    
    return (
      <div className="flex flex-wrap gap-1 mt-2 mb-3">
        {tags.map((tag, i) => (
          <span 
            key={i}
            className="px-2 py-0.5 text-xs rounded-full"
            style={{ 
              backgroundColor: tagColors[tag].bg,
              color: tagColors[tag].text,
              border: `1px solid ${tagColors[tag].border}`
            }}
          >
            {tag.charAt(0).toUpperCase() + tag.slice(1)}
          </span>
        ))}
      </div>
    );
  };

  // Placeholder content for demonstration
  const placeholderContent = (
    <div className="space-y-6">
      <h1 className="text-3xl font-serif font-bold text-[#2C3E50]">Free Educational Library</h1>
      <p className="text-lg text-[#7F8C8D]">
        Welcome to our collection of free resources on peptides and longevity medicine. Select a topic from the 
        navigation menu to begin exploring our educational content.
      </p>
      
      <div className="flex flex-wrap items-center gap-3 mt-4 p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg">
        <span className="text-sm font-medium text-[#2C3E50]">Filter by:</span>
        <div className="flex items-center">
          <button
            onClick={() => toggleTag('general')}
            className={cn(
              "px-2 py-0.5 text-xs rounded-full border cursor-pointer",
              selectedTags.includes('general')
                ? "bg-[#EBF5FB] border-[#3498DB] text-[#3498DB] font-medium"
                : "bg-[#EBF5FB] border-[#3498DB] text-[#3498DB] opacity-70"
            )}
          >
            General
          </button>
          <span className="ml-2 text-xs text-[#7F8C8D]">Introductory content, overviews</span>
        </div>
        <div className="flex items-center">
          <button
            onClick={() => toggleTag('science')}
            className={cn(
              "px-2 py-0.5 text-xs rounded-full border cursor-pointer",
              selectedTags.includes('science')
                ? "bg-[#E8F8F5] border-[#1ABC9C] text-[#1ABC9C] font-medium"
                : "bg-[#E8F8F5] border-[#1ABC9C] text-[#1ABC9C] opacity-70"
            )}
          >
            Science
          </button>
          <span className="ml-2 text-xs text-[#7F8C8D]">Research-focused, detailed mechanisms</span>
        </div>
        <div className="flex items-center">
          <button
            onClick={() => toggleTag('clinical')}
            className={cn(
              "px-2 py-0.5 text-xs rounded-full border cursor-pointer",
              selectedTags.includes('clinical')
                ? "bg-[#FEF9E7] border-[#F1C40F] text-[#F1C40F] font-medium"
                : "bg-[#FEF9E7] border-[#F1C40F] text-[#F1C40F] opacity-70"
            )}
          >
            Clinical
          </button>
          <span className="ml-2 text-xs text-[#7F8C8D]">Application in medical practice</span>
        </div>
        <div className="flex items-center">
          <button
            onClick={() => toggleTag('business')}
            className={cn(
              "px-2 py-0.5 text-xs rounded-full border cursor-pointer",
              selectedTags.includes('business')
                ? "bg-[#F4ECF7] border-[#8E44AD] text-[#8E44AD] font-medium"
                : "bg-[#F4ECF7] border-[#8E44AD] text-[#8E44AD] opacity-70"
            )}
          >
            Business
          </button>
          <span className="ml-2 text-xs text-[#7F8C8D]">Market trends, industry analysis</span>
        </div>
        {selectedTags.length > 0 && (
          <button
            onClick={() => setSelectedTags([])}
            className="text-xs text-[#3498DB] hover:underline ml-auto"
          >
            Clear filters
          </button>
        )}
      </div>
      
      <div className="my-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredItems.length > 0 ? (
          <>
            {filteredItems.filter(item => item.href === 'peptides').map((item, index) => (
              <div key={index} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-2">
                  <div className="p-2 rounded-full bg-[#EBF5FB] text-[#3498DB] mr-3">
                    {item.icon}
                  </div>
                  <h3 className="font-medium text-[#2C3E50]">{item.title}</h3>
                </div>
                {renderTags(item.tags)}
                <p className="text-sm text-[#7F8C8D] mb-4">{item.description}</p>
                <div className="flex items-center">
                  <Link href={`/${item.href}`}>
                    <Button variant="link" className="text-[#3498DB] p-0 h-auto">
                      Explore {item.subItems?.length || 0} topics
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
            
            {filteredItems.filter(item => item.href !== 'peptides').map((item, index) => (
              <div key={index + 1} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-2">
                  <div className="p-2 rounded-full bg-[#EBF5FB] text-[#3498DB] mr-3">
                    {item.icon}
                  </div>
                  <h3 className="font-medium text-[#2C3E50]">{item.title}</h3>
                </div>
                {renderTags(item.tags)}
                <p className="text-sm text-[#7F8C8D] mb-4">{item.description}</p>
                <div className="flex items-center">
                  <span className="italic text-sm text-[#7F8C8D] mr-2">Coming soon</span>
                  <Link href={`/${item.href}`}>
                    <Button variant="link" className="text-[#3498DB] p-0 h-auto">
                      View article
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="col-span-2 p-8 text-center">
            <p className="text-gray-500 mb-4">No articles match the selected filters.</p>
            <Button onClick={() => setSelectedTags([])} variant="outline">
              Clear Filters
            </Button>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-[#EBF5FB] rounded-lg p-6 border border-[#D6EAF8]">
          <h2 className="text-xl font-medium text-[#2C3E50] mb-3">Peptide Science</h2>
          <p className="text-[#7F8C8D] mb-4">
            Start with our foundational peptide content to understand the basics
            of these powerful compounds and their therapeutic applications.
          </p>
          <Link href="/peptides">
            <Button className="bg-[#3498DB] hover:bg-[#2980B9]">Read Peptide Foundations</Button>
          </Link>
        </div>

        <div className="bg-[#F5F5DC] rounded-lg p-6 border border-[#E8E8D6]">
          <h2 className="text-xl font-medium text-[#2C3E50] mb-3">Longevity Medicine</h2>
          <p className="text-[#7F8C8D] mb-4">
            Our team is working on bringing you comprehensive content on longevity medicine practices. 
            Check back soon for in-depth articles and expert insights.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-1 flex">
        {/* Mobile nav toggle */}
        {isMobile && (
          <button 
            onClick={() => setNavOpen(!navOpen)}
            className="fixed bottom-6 right-6 z-40 bg-[#3498DB] text-white p-3 rounded-full shadow-lg"
          >
            {navOpen ? <X size={24} /> : <MenuIcon size={24} />}
          </button>
        )}
        
        {/* Sidebar navigation */}
        <aside 
          className={cn(
            "w-72 bg-[#F8FAFC] border-r border-[#E2E8F0] fixed inset-y-0 pt-16 z-30 transition-transform duration-300 ease-in-out",
            navOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          )}
        >
          <div className="p-4 space-y-4">
            <form onSubmit={handleSearch} className="relative">
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pr-10"
              />
              <button 
                type="submit"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7F8C8D]"
              >
                <Search className="h-4 w-4" />
              </button>
            </form>
            
            <div>
              <h3 className="text-sm font-medium mb-2">Filter by Topic:</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => toggleTag('general')}
                  className={cn(
                    "px-3 py-1 text-xs rounded-full border",
                    selectedTags.includes('general')
                      ? "bg-[#EBF5FB] border-[#3498DB] text-[#3498DB]"
                      : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                  )}
                >
                  General
                </button>
                <button
                  onClick={() => toggleTag('science')}
                  className={cn(
                    "px-3 py-1 text-xs rounded-full border",
                    selectedTags.includes('science')
                      ? "bg-[#E8F8F5] border-[#1ABC9C] text-[#1ABC9C]"
                      : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                  )}
                >
                  Science
                </button>
                <button
                  onClick={() => toggleTag('clinical')}
                  className={cn(
                    "px-3 py-1 text-xs rounded-full border",
                    selectedTags.includes('clinical')
                      ? "bg-[#FEF9E7] border-[#F1C40F] text-[#F1C40F]"
                      : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                  )}
                >
                  Clinical
                </button>
                <button
                  onClick={() => toggleTag('business')}
                  className={cn(
                    "px-3 py-1 text-xs rounded-full border",
                    selectedTags.includes('business')
                      ? "bg-[#F4ECF7] border-[#8E44AD] text-[#8E44AD]"
                      : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                  )}
                >
                  Business
                </button>
              </div>
              {selectedTags.length > 0 && (
                <button
                  onClick={() => setSelectedTags([])}
                  className="text-xs text-[#3498DB] mt-2 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
          
          <Separator />
          
          <ScrollArea className="h-[calc(100vh-180px)]">
            <div className="p-4 space-y-1">
              {filteredNavItems.map((item, i) => (
                <div key={i} className="mb-2">
                  <div className="flex justify-between w-full">
                    <Link 
                      href={`/${item.href}`}
                      className={cn(
                        "flex-1 flex items-center p-2 rounded-md text-left",
                        currentPath === item.href 
                          ? "bg-[#EBF5FB] text-[#3498DB]" 
                          : "hover:bg-gray-100"
                      )}
                    >
                      <span className="mr-2">{item.icon}</span>
                      <div>
                        <span className="font-medium">{item.title}</span>
                        {item.tags && item.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {item.tags.map((tag, idx) => {
                              const tagColors: Record<TagType, { bg: string, text: string }> = {
                                'general': { bg: '#EBF5FB', text: '#3498DB' },
                                'science': { bg: '#E8F8F5', text: '#1ABC9C' },
                                'clinical': { bg: '#FEF9E7', text: '#F1C40F' },
                                'business': { bg: '#F4ECF7', text: '#8E44AD' }
                              };
                              return (
                                <span 
                                  key={idx}
                                  className="px-1.5 py-0.5 text-[10px] rounded-full"
                                  style={{ 
                                    backgroundColor: tagColors[tag].bg,
                                    color: tagColors[tag].text
                                  }}
                                >
                                  {tag}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </Link>
                    <button
                      onClick={() => toggleExpand(i)}
                      className="p-2 hover:bg-gray-100 rounded-md"
                    >
                      <ChevronRight 
                        className={cn(
                          "h-4 w-4 transition-transform", 
                          item.expanded ? "rotate-90" : ""
                        )} 
                      />
                    </button>
                  </div>
                  
                  {item.expanded && item.subItems && (
                    <div className="pl-10 mt-1 space-y-1">
                      {item.subItems.map((subItem, j) => (
                        <Link
                          key={j}
                          href={`/${subItem.href}`}
                          className={cn(
                            "block py-1 px-2 rounded-md text-sm",
                            location.includes(subItem.href)
                              ? "bg-[#EBF5FB] text-[#3498DB]"
                              : "text-[#7F8C8D] hover:bg-gray-100"
                          )}
                        >
                          {subItem.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </aside>
        
        {/* Main content */}
        <main 
          className={cn(
            "flex-1 transition-margin duration-300 ease-in-out",
            navOpen ? "md:ml-72" : ""
          )}
        >
          <div className="container max-w-4xl mx-auto px-4 py-8">
            {placeholderContent}
          </div>
        </main>
      </div>
      
      <Footer />
    </div>
  );
}